var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { RoleEnum } from "../types/RoleEnum";
/**
 * DB-946: the technical ExportApiClient account must see nothing beyond the title export, so the role is exclusive
 * across the whole user - no other role in the same membership nor in any other one (mod-br-users rejects it with
 * 422 / VALIDATION_ROLE_EXCLUSIVITY). The forms disable the checkboxes that would break the rule.
 *
 * @param membershipRoles the role codes of every membership on the form, in form order
 * @param index the membership the checkbox belongs to
 * @param role the role code of the checkbox
 * @returns whether the checkbox is disabled; a ticked role is never locked, so it can always be unticked
 */
export var isRoleLockedByExportApiClient = function (membershipRoles, index, role) {
    var _a;
    var own = (_a = membershipRoles[index]) !== null && _a !== void 0 ? _a : [];
    if (own.includes(role)) {
        return false;
    }
    var others = membershipRoles.filter(function (_, i) { return i !== index; });
    if (role === RoleEnum.EXPORT_API_CLIENT) {
        return own.length > 0 || others.some(function (roles) { return roles.length > 0; });
    }
    return (own.includes(RoleEnum.EXPORT_API_CLIENT) ||
        others.some(function (roles) { return roles.includes(RoleEnum.EXPORT_API_CLIENT); }));
};
var holdsExport = function (membership) { var _a; return !!((_a = membership === null || membership === void 0 ? void 0 : membership.roles) === null || _a === void 0 ? void 0 : _a.includes(RoleEnum.EXPORT_API_CLIENT)); };
/**
 * The server checks the exclusivity after every single membership save, so a valid end state must be reached by
 * valid steps: memberships that give the export role up are saved first, then the others, and the ones that hold it
 * last.
 *
 * @param memberships the memberships about to be saved
 * @param stored the memberships as loaded from the server
 * @returns the memberships in save order
 */
export var orderMembershipsForSave = function (memberships, stored) {
    if (stored === void 0) { stored = []; }
    var storedHoldsExport = function (membership) {
        return holdsExport(stored.find(function (s) { return s.organization.id === membership.organization.id; }));
    };
    var rank = function (membership) {
        if (holdsExport(membership))
            return 2;
        return storedHoldsExport(membership) ? 0 : 1;
    };
    return __spreadArray([], memberships, true).sort(function (a, b) { return rank(a) - rank(b); });
};
/**
 * Splits the memberships removed on the form into those deleted before the role changes are saved and those deleted
 * after them (the order before DB-946, so a failing role change never costs a membership needlessly). Only a removal
 * the exclusivity needs goes first: a membership holding the export role, or - when the user will hold the export
 * role - a membership with any role. A membership removed and added back on the form is not deleted at all; its save
 * updates it.
 *
 * @param removedIds the organization ids removed on the form
 * @param stored the memberships as loaded from the server
 * @param memberships the memberships on the form (the target state)
 * @returns the organization ids to delete before and after the role changes
 */
export var planMembershipRemovals = function (removedIds, stored, memberships) {
    var storedById = function (orgId) { return stored.find(function (m) { return m.organization.id === orgId; }); };
    var toDelete = Array.from(new Set(removedIds)).filter(function (orgId) {
        return !!storedById(orgId) && !memberships.some(function (m) { return m.organization.id === orgId; });
    });
    var userWillHoldExport = memberships.some(holdsExport);
    var neededFirst = function (orgId) {
        var _a, _b;
        var membership = storedById(orgId);
        return holdsExport(membership) || (userWillHoldExport && ((_b = (_a = membership === null || membership === void 0 ? void 0 : membership.roles) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0);
    };
    return {
        beforeRoleChanges: toDelete.filter(neededFirst),
        afterRoleChanges: toDelete.filter(function (orgId) { return !neededFirst(orgId); }),
    };
};
